import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

export default function ProjectsGallery() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.getProjects().then((res) => setProjects(res.data)).catch(() => {});
  }, []);

  return (
    <section
      id="proyectos"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12 lg:px-24"
      data-testid="projects-section"
    >
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20 mb-16 md:mb-24" ref={ref}>
          <div className="md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-400"
            >
              Portafolio
            </motion.p>
          </div>
          <div className="md:col-span-10">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-3xl md:text-5xl lg:text-6xl font-normal text-neutral-900 tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Proyectos seleccionados
            </motion.h2>
          </div>
        </div>

        {/* Project cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {projects.map((project, i) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 50, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => navigate(`/proyecto/${project.id}`)}
              className="group cursor-pointer relative overflow-hidden rounded-2xl bg-neutral-100"
              data-testid={`project-card-${i}`}
            >
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={project.cover_image || 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&q=80'}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&q=80'; }}
                />
                {/* Dark overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
              </div>

              {/* Text overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-white/50 text-[10px] tracking-[0.25em] uppercase font-semibold mb-1">
                  {project.category}
                </p>
                <h3
                  className="text-white text-xl md:text-2xl font-medium tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {project.title}
                </h3>
                <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                  <span className="h-px w-6 bg-white/50" />
                  <span className="text-white/70 text-xs tracking-wide">Ver proyecto</span>
                  <ArrowRight size={12} className="text-white/70" strokeWidth={2} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
